import { TrackingInfos, TrackingType } from "@/app/stores/[id]/storefront/types/tracking";

export const TRACKING_TYPE_PIXEL = [
    TrackingType.FACEBOOK,
    TrackingType.TIKTOK,
    TrackingType.SNAPCHAT,
    TrackingType.PINTEREST,
];

export const TRACKING_CONFIGS: {
    [K in TrackingType]: TrackingInfos<K>;
} = {
    [TrackingType.FACEBOOK]: {
        name: 'Meta Pixel',
        icon: '/integrations/fb-icon.svg',
        alt: 'Meta Pixel Logo',
        pixelIdPlaceholder: '123456789012345',
        pixelIdDescription: 'Your Facebook Pixel ID is a 15-16 digit number',
        pixelIdRegex: /^\d{15,16}$/,
        events: [
            { key: 'PageView', label: 'Page View' },
            { key: 'InitiateCheckout', label: 'Initiate Checkout' },
            { key: 'Purchase', label: 'Purchase' },
            { key: 'AddToCart', label: 'Add to Cart' },
            { key: 'ViewContent', label: 'View Content' },
        ],
        info: 'Meta Pixel helps you track conversions from Facebook Ads, build targeted audiences, and improve your ad performance.',
        setupSteps: [
            'Go to Facebook Events Manager',
            'Select your pixel from the Data Sources tab',
            'Click on "Settings"',
            'Your Pixel ID will be displayed at the top',
        ],
    },
    [TrackingType.TIKTOK]: {
        name: 'TikTok Pixel',
        icon: '/integrations/tiktok-icon.svg',
        alt: 'TikTok Pixel Logo',
        pixelIdPlaceholder: 'ABCDEFGHIJ1234567890',
        pixelIdDescription: 'Your TikTok Pixel ID is a string of letters and numbers',
        pixelIdRegex: /^[a-zA-Z0-9]+$/,
        events: [
            { key: 'PageView', label: 'Page View' },
            { key: 'InitiateCheckout', label: 'Initiate Checkout' },
            { key: 'Purchase', label: 'Purchase' },
            { key: 'AddToCart', label: 'Add to Cart' },
            { key: 'ViewContent', label: 'View Content' },
        ],
        info: 'TikTok Pixel helps you track conversions from TikTok Ads, build targeted audiences, and improve your ad performance.',
        setupSteps: [
            'Go to TikTok Ads Manager',
            'Navigate to "Assets" → "Events"',
            'Select your pixel',
            'Your Pixel ID will be shown in the pixel details',
        ],
    },
    [TrackingType.SNAPCHAT]: {
        name: 'Snapchat Pixel',
        icon: '/integrations/snapchat.svg',
        alt: 'Snapchat Pixel Logo',
        pixelIdPlaceholder: 'Enter your Snapchat Pixel ID',
        pixelIdDescription: 'Your Snapchat Pixel ID is an alphanumeric string that begins with your account\'s prefix',
        pixelIdRegex: /^[a-zA-Z0-9-]+$/,
        info: 'Snapchat Pixel helps you track conversions from Snapchat Ads, build targeted audiences, and improve your ad performance.',
        setupSteps: [
            'Go to your Snapchat Ads Manager',
            'Click on "Events Manager" and select "Pixel"',
            'Select your pixel or create a new one',
            'Your Pixel ID will be displayed in the pixel details',
        ],
        events: [
            { key: 'PageView', label: 'Page View' },
            { key: 'InitiateCheckout', label: 'Initiate Checkout' },
            { key: 'Purchase', label: 'Purchase' },
            { key: 'AddToCart', label: 'Add to Cart' },
            { key: 'ViewContent', label: 'View Content' },
        ],
    },
    [TrackingType.PINTEREST]: {
        name: 'Pinterest Tag',
        icon: '/integrations/pinterest.svg',
        alt: 'Pinterest Tag Logo',
        pixelIdPlaceholder: '1234567890123',
        pixelIdDescription: 'Your Pinterest Tag ID is a 13-digit number found under "Unique Tag ID"',
        pixelIdRegex: /^\d{13}$/,
        events: [
            { key: 'PageView', label: 'Page View' },
            { key: 'InitiateCheckout', label: 'Initiate Checkout' },
            { key: 'Purchase', label: 'Purchase' },
            { key: 'AddToCart', label: 'Add to Cart' },
            { key: 'ViewContent', label: 'View Content' },
        ],
        info: 'Pinterest Tag helps you track conversions from Pinterest Ads, build targeted audiences, and measure your ad performance.',
        setupSteps: [
            'Go to Pinterest Ads Manager (ads.pinterest.com)',
            'Navigate to "Ads" → "Conversions" from the top menu',
            'Click on "Pinterest tag" in the left sidebar, then "Tag Manager"',
            'Your Tag ID is displayed in the "Tag name" box at the top',
        ],
    },
    [TrackingType.GTM]: {
        name: 'Google Tag Manager',
        icon: '/integrations/gtm.svg',
        alt: 'Google Tag Manager Logo',
        containerIdPlaceholder: 'GTM-XXXXXX, AW-XXXXXXXXXX, or G-XXXXXXXXXX',
        containerIdDescription: 'Enter your Google Tag Manager (GTM-), Google Ads (AW-), or GA4 (G-) ID.',
        containerIdRegex: /^(GTM|AW|G)-[A-Z0-9]+$/,
        events: [
            { key: 'PageView', label: 'Page View' },
            { key: 'InitiateCheckout', label: 'Initiate Checkout' },
            { key: 'Purchase', label: 'Purchase' },
            { key: 'AddToCart', label: 'Add to Cart' },
            { key: 'ViewContent', label: 'View Content' },
        ],
    },
    [TrackingType.CLARITY]: {
        name: 'Microsoft Clarity',
        icon: '/integrations/clarity.svg',
        alt: 'Microsoft Clarity Logo',
        projectIdPlaceholder: 'abcd1234ef',
        projectIdDescription: 'Your Clarity project ID is the short alphanumeric code in the tracking snippet (clarity.ms/tag/<project id>).',
        // Soft rule, surfaced as a warning: ids we have seen are ten lowercase
        // alphanumerics, but an unusual-yet-working one must not lock a merchant out.
        projectIdRegex: /^[a-z0-9]{6,15}$/,
        events: [
            { key: 'PageView', label: 'Page View' },
            { key: 'InitiateCheckout', label: 'Initiate Checkout' },
            { key: 'Purchase', label: 'Purchase' },
            { key: 'AddToCart', label: 'Add to Cart' },
            { key: 'ViewContent', label: 'View Content' },
        ],
        info: 'Clarity records sessions and heatmaps so you can watch how shoppers behave on this step. Session recording starts on its own; the events below let you filter recordings on funnel milestones.',
        setupSteps: [
            'Open clarity.microsoft.com and select your project',
            'Go to Settings → Overview',
            'Copy the project ID shown there (also visible in the install snippet)',
        ],
    }
};

/**
 * An event with no stored key is ON: the SDK sends any event absent from an
 * entry's events map. Keys the form no longer lists (legacy Snapchat
 * Search...) stay in the result untouched so a save does not drop them.
 */
export const withDefaultPixelEvents = <T extends { events?: object }>(type: TrackingType, values: T): T & { events: Record<string, boolean> } => {
    const stored = Object.fromEntries(
        Object.entries(values.events ?? {}).filter(([, enabled]) => typeof enabled === 'boolean'),
    );
    const defaults = Object.fromEntries(
        (TRACKING_CONFIGS[type].events ?? []).map(({ key }) => [key, true]),
    );
    return { ...values, events: { ...defaults, ...stored } };
};
